import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Users, Award } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function HomePage() {
  const features = [
    {
      icon: <Star size={28} />,
      title: "Elite Representation",
      description: "We represent carefully selected talent across film, television, theater, and commercial work."
    },
    {
      icon: <Users size={28} />,
      title: "Industry Connections",
      description: "Direct relationships with top casting directors, producers, and industry decision-makers."
    },
    {
      icon: <Award size={28} />,
      title: "Career Development",
      description: "Comprehensive support including coaching, branding, and strategic career planning."
    }
  ];

  return (
    <div>
      <Header />
      
      {/* Hero Section */}
      <section className="hero" data-testid="hero-section">
        <div 
          className="hero-bg"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1774016591378-925354a456ee?w=1920&q=80)` }}
        />
        <div className="hero-overlay" />
        
        <motion.div 
          className="hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <p className="hero-subtitle">Premier Talent Management</p>
          <h1 className="hero-title">Woodhouse & Archer</h1>
          <p className="hero-description">
            Where extraordinary talent meets exceptional opportunity. 
            We represent the next generation of actors, models, and performers 
            who are shaping the future of entertainment.
          </p>
          <div className="hero-buttons">
            <Link to="/roster" className="btn btn-primary" data-testid="hero-cta-roster">
              View Our Talent
              <ArrowRight size={18} />
            </Link>
            <Link to="/register" className="btn btn-secondary" data-testid="hero-cta-register">
              Join Our Roster
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="section section-dark" data-testid="features-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="section-subtitle">Why Choose Us</p>
            <h2 className="section-title">Excellence in Representation</h2>
            <p className="section-description">
              With decades of combined experience, we've helped launch and sustain the careers 
              of some of the industry's most recognized names.
            </p>
          </motion.div>

          <div className="services-grid">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="card service-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                data-testid={`feature-card-${index}`}
              >
                <div className="service-icon">{feature.icon}</div>
                <h3 className="service-title">{feature.title}</h3>
                <p className="service-description">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Instagram Feed Section */}
      <section className="section section-dark" data-testid="instagram-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="section-subtitle">Follow Us</p>
            <h2 className="section-title">@woodhouseandarcher</h2>
            <p className="section-description">
              Stay connected with our latest projects, talent showcases, and behind-the-scenes moments.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            dangerouslySetInnerHTML={{
              __html: '<behold-widget feed-id="JFIxBxpga1Ucu5o3sCR5"></behold-widget>'
            }}
          />

          <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
            <a 
              href="https://instagram.com/woodhouseandarcher" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn btn-secondary"
              data-testid="instagram-follow-btn"
            >
              Follow on Instagram
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section-surface" data-testid="cta-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="section-subtitle">Ready to Start?</p>
            <h2 className="section-title">Join Our Roster</h2>
            <p className="section-description">
              Are you a talented performer looking for representation? 
              We're always searching for exceptional talent to join our roster.
            </p>
            <div style={{ marginTop: '2rem' }}>
              <Link to="/register" className="btn btn-primary" data-testid="cta-apply-btn">
                Apply Now
                <ArrowRight size={18} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
